class PaworaTheme {
  constructor() {
    this.drawer = document.querySelector('[data-cart-drawer]');
    this.toast = document.getElementById('Toast');
    this.cartUpdateTimers = new WeakMap();
    this.cartLineVersions = new Map();
    this.cartNoteTimer = null;
    this.toastTimer = null;
    this.bindEvents();
    this.initProductGalleries();
    this.initProductVariants();
    this.initProductDescriptions();
    this.initQuantityInputs();
    this.initCustomSelects();
  }

  bindEvents() {
    document.addEventListener('click', (event) => {
      const cartTrigger = event.target.closest('[data-cart-open]');
      const cartClose = event.target.closest('[data-cart-close]');
      const cartRemove = event.target.closest('[data-cart-remove]');
      const cartPageRemove = event.target.closest('[data-cart-page-remove]');
      const quickAdd = event.target.closest('[data-quick-add]');
      const menuToggle = event.target.closest('[data-menu-toggle]');
      const searchToggle = event.target.closest('[data-search-toggle]');
      const customSelect = event.target.closest('[data-custom-select]');
      const facetsOpen = event.target.closest('[data-facets-open]');
      const facetsClose = event.target.closest('[data-facets-close]');
      const gridView = event.target.closest('[data-grid-columns]');
      const showcaseFilter = event.target.closest('.showcase-filter');
      const productShare = event.target.closest('[data-product-share]');
      if (cartTrigger) { event.preventDefault(); this.openCart(); }
      if (cartClose || event.target.matches('.cart-drawer__overlay')) this.closeCart();
      if (cartRemove) { event.preventDefault(); this.removeCartItem(cartRemove); }
      if (cartPageRemove) { event.preventDefault(); this.removeCartPageItem(cartPageRemove); }
      if (quickAdd) { event.preventDefault(); this.quickAdd(quickAdd); }
      if (menuToggle) this.toggleMenu(menuToggle);
      if (searchToggle) document.querySelector('[data-search-panel]')?.toggleAttribute('open');
      if (facetsOpen) this.openFacets(facetsOpen);
      if (facetsClose) this.closeFacets();
      if (gridView) this.setShowcaseGrid(gridView);
      if (productShare) this.shareProduct(productShare);
      if (!showcaseFilter || event.target.closest('.showcase-filter > summary')) {
        document.querySelectorAll('.showcase-filter[open]').forEach(filter => {
          if (filter !== showcaseFilter) filter.removeAttribute('open');
        });
      }
      if (!customSelect) this.closeCustomSelects();
    });

    document.addEventListener('change', event => {
      const cartQuantity = event.target.closest('[data-cart-quantity]');
      if (cartQuantity) this.scheduleCartLineUpdate(cartQuantity);

      const cartNote = event.target.closest('[data-cart-page] [name="note"]');
      if (cartNote) this.scheduleCartNoteUpdate(cartNote);

      const sortSelect = event.target.closest('[data-sort-select]');
      if (!sortSelect) return;
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sortSelect.value);
      url.searchParams.delete('page');
      window.location.assign(url.toString());
    });

    document.addEventListener('input', event => {
      const cartNote = event.target.closest('[data-cart-page] [name="note"]');
      if (cartNote) this.scheduleCartNoteUpdate(cartNote);
    });

    document.addEventListener('submit', (event) => {
      const form = event.target.closest('[data-product-form]');
      if (!form) return;
      event.preventDefault();
      this.addFormToCart(form);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.closeCart();
        this.closeFacets();
        document.querySelectorAll('.showcase-filter[open]').forEach(filter => filter.removeAttribute('open'));
        this.closeCustomSelects(true);
      }
    });

    document.addEventListener('shopify:section:load', event => {
      this.initCustomSelects(event.target);
      this.initProductGalleries(event.target);
      this.initProductVariants(event.target);
      this.initProductDescriptions(event.target);
    });

    window.addEventListener('pageshow', event => {
      if (event.persisted && this.drawer) this.refreshCart(false).catch(() => {});
    });
  }

  async shareProduct(button) {
    const shareData = { title: document.title, url: window.location.href };
    try {
      if (navigator.share) await navigator.share(shareData);
      else {
        await navigator.clipboard.writeText(shareData.url);
        this.showToast(button.dataset.shareSuccess || 'Product link copied');
      }
    } catch (error) {
      if (error?.name !== 'AbortError') this.showToast(button.dataset.shareSuccess || 'Product link copied');
    }
  }

  toggleMenu(button) {
    const menu = document.querySelector('[data-mobile-menu]');
    if (!menu) return;
    const shouldOpen = !menu.hasAttribute('open');
    document.querySelectorAll('[data-menu-toggle]').forEach(toggle => {
      toggle.setAttribute('aria-expanded', String(shouldOpen));
    });
    menu.toggleAttribute('open', shouldOpen);
    document.body.classList.toggle('menu-open', shouldOpen);
  }

  openFacets(button) {
    const form = document.querySelector('[data-facets-form]');
    if (!form) return;
    form.classList.add('is-open');
    document.body.classList.add('facets-open');
    button.setAttribute('aria-expanded', 'true');
    form.querySelector('.facets__close')?.focus();
  }

  closeFacets() {
    const form = document.querySelector('[data-facets-form]');
    const trigger = document.querySelector('[data-facets-open]');
    if (!form?.classList.contains('is-open')) return;
    form.classList.remove('is-open');
    document.body.classList.remove('facets-open');
    trigger?.setAttribute('aria-expanded', 'false');
    trigger?.focus();
  }

  setShowcaseGrid(button) {
    const grid = document.querySelector('[data-showcase-grid]');
    if (!grid) return;
    const columns = button.dataset.gridColumns;
    grid.dataset.columns = columns;
    document.querySelectorAll('[data-grid-columns]').forEach(viewButton => {
      const active = viewButton === button;
      viewButton.classList.toggle('is-active', active);
      viewButton.setAttribute('aria-pressed', String(active));
    });
  }

  async quickAdd(button) {
    if (!button.dataset.variantId || button.classList.contains('is-loading')) return;
    button.classList.add('is-loading');
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    try {
      await this.addToCart({ id: Number(button.dataset.variantId), quantity: 1 });
    } finally {
      button.classList.remove('is-loading');
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }

  async addFormToCart(form) {
    const button = form.querySelector('[type="submit"]');
    button?.classList.add('is-loading');
    button?.setAttribute('aria-busy', 'true');
    const body = Object.fromEntries(new FormData(form));
    body.quantity = Number(body.quantity || 1);
    await this.addToCart(body);
    button?.classList.remove('is-loading');
    button?.removeAttribute('aria-busy');
  }

  async addToCart(body) {
    const previousCount = this.getCartCount();
    const quantity = Number(body.quantity || 1);
    this.setCartCount(previousCount + quantity);
    this.openCart();
    this.setCartUpdating(true);

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          ...body,
          sections: 'cart-drawer',
          sections_url: window.location.pathname
        })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || 'Unable to add this product');

      if (result.sections?.['cart-drawer']) this.applyCartSection(result.sections['cart-drawer'], true);
      else await this.refreshCart(true);
      this.showToast('Added to cart');
    } catch (error) {
      this.setCartCount(previousCount);
      this.showToast(error.message);
    } finally {
      this.setCartUpdating(false);
    }
  }

  async removeCartItem(button) {
    if (button.classList.contains('is-loading')) return;

    const line = button.closest('[data-cart-line]');
    const key = button.dataset.lineKey;
    const lineQuantity = Number(line?.dataset.lineQuantity || 1);
    const previousCount = this.getCartCount();
    if (!key) return;

    button.classList.add('is-loading');
    button.disabled = true;
    line?.classList.add('is-removing');
    this.setCartCount(Math.max(0, previousCount - lineQuantity));

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          id: key,
          quantity: 0,
          sections: 'cart-drawer',
          sections_url: window.location.pathname
        })
      });
      const cart = await response.json();
      if (!response.ok) throw new Error(cart.description || 'Unable to remove this product');

      this.setCartCount(cart.item_count);
      if (cart.sections?.['cart-drawer']) this.applyCartSection(cart.sections['cart-drawer'], true);
      else await this.refreshCart(true);
    } catch (error) {
      this.setCartCount(previousCount);
      button.classList.remove('is-loading');
      button.disabled = false;
      line?.classList.remove('is-removing');
      this.showToast(error.message);
    }
  }

  removeCartPageItem(button) {
    const line = button.closest('[data-cart-page-line]');
    const input = line?.querySelector('[data-cart-quantity]');
    if (!input) return;
    input.value = '0';
    this.scheduleCartLineUpdate(input, 0);
  }

  scheduleCartLineUpdate(input, delay = 350) {
    const currentTimer = this.cartUpdateTimers.get(input);
    if (currentTimer) clearTimeout(currentTimer);

    const quantity = Math.max(0, Number.parseInt(input.value, 10) || 0);
    input.value = String(quantity);
    input.closest('[data-cart-page-line]')?.classList.add('is-pending');

    const timer = setTimeout(() => {
      this.cartUpdateTimers.delete(input);
      this.updateCartLine(input);
    }, delay);
    this.cartUpdateTimers.set(input, timer);
  }

  async updateCartLine(input) {
    const cartPage = input.closest('[data-cart-page]');
    const line = input.closest('[data-cart-page-line]');
    const sectionId = cartPage?.dataset.sectionId;
    const key = input.dataset.lineKey;
    const quantity = Math.max(0, Number(input.value || 0));
    const previousValue = input.dataset.previousValue || '1';
    const noteValue = cartPage?.querySelector('[name="note"]')?.value || '';
    if (!cartPage || !sectionId || !key) return;
    const requestVersion = (this.cartLineVersions.get(key) || 0) + 1;
    this.cartLineVersions.set(key, requestVersion);
    line?.classList.remove('is-pending');
    line?.classList.add('is-updating');

    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          id: key,
          quantity,
          sections: `${sectionId},cart-drawer`,
          sections_url: window.location.pathname
        })
      });
      const cart = await response.json();
      if (!response.ok) throw new Error(cart.description || 'Unable to update your cart');
      if (this.cartLineVersions.get(key) !== requestVersion) return;

      const sectionHtml = cart.sections?.[sectionId];
      if (!this.applyCartPageSection(sectionHtml, noteValue)) {
        throw new Error('Unable to refresh your cart');
      }
      if (cart.sections?.['cart-drawer']) this.applyCartSection(cart.sections['cart-drawer'], false);
      this.setCartCount(cart.item_count);
      this.cartLineVersions.delete(key);
    } catch (error) {
      if (this.cartLineVersions.get(key) !== requestVersion) return;
      input.value = previousValue;
      line?.classList.remove('is-pending', 'is-updating');
      this.cartLineVersions.delete(key);
      this.showToast(error.message);
    }
  }

  scheduleCartNoteUpdate(input) {
    clearTimeout(this.cartNoteTimer);
    const cartPage = input.closest('[data-cart-page]');
    cartPage?.classList.add('is-saving-note');
    this.cartNoteTimer = setTimeout(() => this.updateCartNote(input), 500);
  }

  async updateCartNote(input) {
    const cartPage = input.closest('[data-cart-page]');
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ note: input.value })
      });
      if (!response.ok) throw new Error('Unable to save your note');
      cartPage?.classList.add('is-note-saved');
      setTimeout(() => cartPage?.classList.remove('is-note-saved'), 1400);
    } catch (error) {
      this.showToast(error.message);
    } finally {
      cartPage?.classList.remove('is-saving-note');
    }
  }

  applyCartPageSection(sectionHtml, noteValue = '') {
    if (!sectionHtml) return false;
    const documentFragment = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const fresh = documentFragment.querySelector('[data-cart-page]');
    const current = document.querySelector('[data-cart-page]');
    if (!fresh || !current) return false;

    const freshNote = fresh.querySelector('[name="note"]');
    if (freshNote) freshNote.value = noteValue;
    current.replaceWith(fresh);
    return true;
  }

  getCartCount() {
    const drawerCount = Number(this.drawer?.dataset.cartItemCount);
    if (Number.isFinite(drawerCount)) return drawerCount;
    return Number(document.querySelector('[data-cart-count]')?.textContent || 0);
  }

  setCartCount(count) {
    if (this.drawer) this.drawer.dataset.cartItemCount = String(count);
    document.querySelectorAll('[data-cart-count]').forEach(element => {
      element.textContent = String(count);
      element.hidden = count === 0;
    });
  }

  setCartUpdating(updating) {
    this.drawer?.classList.toggle('is-updating', updating);
    this.drawer?.setAttribute('aria-busy', String(updating));
  }

  applyCartSection(sectionHtml, keepOpen = false) {
    if (!sectionHtml || !this.drawer) return false;
    const documentFragment = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const fresh = documentFragment.querySelector('[data-cart-drawer]');
    if (!fresh) return false;

    this.drawer.innerHTML = fresh.innerHTML;
    this.drawer.dataset.cartItemCount = fresh.dataset.cartItemCount || '0';
    this.setCartCount(Number(this.drawer.dataset.cartItemCount));
    if (keepOpen) this.openCart();
    return true;
  }

  async refreshCart(keepOpen = false) {
    const [sectionResponse, cartResponse] = await Promise.all([
      fetch(`${window.Shopify.routes.root}?section_id=cart-drawer`),
      fetch(`${window.Shopify.routes.root}cart.js`, { headers: { 'Accept': 'application/json' } })
    ]);
    const [sectionHtml, cart] = await Promise.all([sectionResponse.text(), cartResponse.json()]);
    this.applyCartSection(sectionHtml, keepOpen);
    this.setCartCount(cart.item_count);
  }

  openCart() { this.drawer?.setAttribute('open', ''); document.body.classList.add('drawer-open'); }
  closeCart() { this.drawer?.removeAttribute('open'); document.body.classList.remove('drawer-open'); }
  showToast(message) {
    if (!this.toast) return;
    clearTimeout(this.toastTimer);
    this.toast.textContent = message;
    this.toast.classList.add('is-visible');
    this.toastTimer = setTimeout(() => this.toast.classList.remove('is-visible'), 2800);
  }

  initProductGalleries(scope = document) {
    scope.querySelectorAll('[data-product-section]').forEach(section => {
      if (section.hasAttribute('data-gallery-ready')) return;

      const gallery = section.querySelector('[data-product-gallery]');
      const slides = Array.from(section.querySelectorAll('[data-gallery-slide]'));
      const thumbs = Array.from(section.querySelectorAll('[data-gallery-thumb]'));
      const current = section.querySelector('[data-gallery-current]');
      if (!gallery || !slides.length) return;

      section.setAttribute('data-gallery-ready', '');
      const setActive = index => {
        thumbs.forEach((thumb, thumbIndex) => {
          const active = thumbIndex === index;
          thumb.classList.toggle('is-active', active);
          thumb.setAttribute('aria-current', String(active));
        });
        if (current) current.textContent = String(index + 1);
      };

      thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => {
        const target = section.querySelector(`#${CSS.escape(thumb.dataset.target)}`);
        if (!target) return;
        gallery.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
        setActive(index);
      }));

      let scrollFrame;
      gallery.addEventListener('scroll', () => {
        cancelAnimationFrame(scrollFrame);
        scrollFrame = requestAnimationFrame(() => {
          const index = slides.reduce((closest, slide, slideIndex) => {
            return Math.abs(slide.offsetLeft - gallery.scrollLeft) < Math.abs(slides[closest].offsetLeft - gallery.scrollLeft)
              ? slideIndex
              : closest;
          }, 0);
          setActive(index);
        });
      }, { passive: true });
    });
  }

  initProductVariants(scope = document) {
    scope.querySelectorAll('[data-product-form]:not([data-variants-ready])').forEach(form => {
      const variantsElement = form.querySelector('[data-product-variants]');
      const variantSelect = form.querySelector('[data-variant-select]');
      const optionGroups = Array.from(form.querySelectorAll('[data-option-position]'));
      if (!variantsElement || !variantSelect || !optionGroups.length) return;

      let variants;
      try { variants = JSON.parse(variantsElement.textContent); }
      catch (error) { return; }
      form.setAttribute('data-variants-ready', '');

      const section = form.closest('[data-product-section]');
      const submitButton = form.querySelector('[data-product-submit]');
      const priceContainer = section?.querySelector('[data-product-price]');
      const stockIndicator = section?.querySelector('[data-product-stock]');

      const updatePrice = variant => {
        const price = priceContainer?.querySelector('.price');
        if (!price) return;
        const currentPrice = price.querySelector('span:first-child');
        if (currentPrice) currentPrice.textContent = variant.price;

        let comparePrice = price.querySelector('.price__compare');
        if (variant.compareAtPrice) {
          if (!comparePrice) {
            comparePrice = document.createElement('span');
            comparePrice.className = 'price__compare';
            price.append(comparePrice);
          }
          comparePrice.textContent = variant.compareAtPrice;
        } else {
          comparePrice?.remove();
        }

        let saleBadge = priceContainer.querySelector('.product__sale-badge');
        const discount = variant.compareAtPriceAmount && variant.priceAmount
          ? Math.round((variant.compareAtPriceAmount - variant.priceAmount) * 100 / variant.compareAtPriceAmount)
          : 0;
        if (discount > 0) {
          if (!saleBadge) {
            saleBadge = document.createElement('span');
            saleBadge.className = 'product__sale-badge';
            priceContainer.append(saleBadge);
          }
          saleBadge.textContent = `Save ${discount}%`;
        } else {
          saleBadge?.remove();
        }
      };

      const showVariantMedia = mediaId => {
        if (!mediaId || !section) return;
        const targetId = `Media-${mediaId}`;
        const thumb = Array.from(section.querySelectorAll('[data-gallery-thumb]')).find(button => button.dataset.target === targetId);
        if (thumb) {
          thumb.click();
          return;
        }
        const gallery = section.querySelector('[data-product-gallery]');
        const target = document.getElementById(targetId);
        if (gallery && target) gallery.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
      };

      const setVariant = (variant, updateUrl = true) => {
        if (!variant) {
          if (submitButton) {
            submitButton.disabled = true;
            submitButton.textContent = submitButton.dataset.soldOutLabel;
          }
          return;
        }

        variantSelect.value = String(variant.id);
        variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
        updatePrice(variant);
        showVariantMedia(variant.featuredMediaId);

        if (submitButton) {
          submitButton.disabled = !variant.available;
          submitButton.textContent = variant.available ? submitButton.dataset.addLabel : submitButton.dataset.soldOutLabel;
        }
        stockIndicator?.classList.toggle('is-unavailable', !variant.available);
        form.querySelector('.product-form__checkout')?.toggleAttribute('hidden', !variant.available);

        if (updateUrl && window.history?.replaceState) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        }
      };

      const findSelectedVariant = () => {
        const selectedValues = optionGroups.map(group => group.querySelector('input:checked')?.value);
        return variants.find(variant => variant.options.every((option, index) => String(option) === selectedValues[index]));
      };

      optionGroups.forEach((group, groupIndex) => {
        group.querySelectorAll('.product-option__input').forEach(input => {
          const valueHasAvailableVariant = variants.some(variant => variant.available && String(variant.options[groupIndex]) === input.value);
          input.disabled = !valueHasAvailableVariant;
          input.nextElementSibling?.classList.toggle('is-disabled', !valueHasAvailableVariant);
        });

        group.addEventListener('change', event => {
          if (!event.target.matches('.product-option__input')) return;
          const selectedLabel = group.querySelector('[data-selected-option]');
          if (selectedLabel) selectedLabel.textContent = event.target.value;
          setVariant(findSelectedVariant());
        });
      });

      const initialVariant = variants.find(variant => String(variant.id) === variantSelect.value) || findSelectedVariant();
      setVariant(initialVariant, false);
    });
  }

  initProductDescriptions(scope = document) {
    scope.querySelectorAll('[data-product-description]').forEach(description => {
      if (description.hasAttribute('data-description-ready')) return;

      const images = Array.from(description.querySelectorAll('img'));
      description.setAttribute('data-description-ready', '');
      if (!images.length) return;

      const mediaGrid = document.createElement('div');
      mediaGrid.className = 'product-details__media';
      mediaGrid.setAttribute('aria-label', 'Product photos');

      images.forEach((image, index) => {
        const originalParent = image.parentElement;
        const figure = document.createElement('figure');
        figure.className = 'product-details__figure';
        if (index === 0) figure.classList.add('product-details__figure--featured');

        image.removeAttribute('style');
        image.removeAttribute('width');
        image.removeAttribute('height');
        image.loading = 'lazy';
        image.decoding = 'async';
        figure.append(image);
        mediaGrid.append(figure);

        if (originalParent && originalParent !== description && !originalParent.textContent.trim() && !originalParent.querySelector('img, video, iframe')) {
          originalParent.remove();
        }
      });

      description.insertAdjacentElement('afterend', mediaGrid);
      description.classList.add('product__description--with-media');
      description.parentElement?.classList.add('has-media');
    });
  }

  initQuantityInputs() {
    document.addEventListener('click', event => {
      const button = event.target.closest('[data-quantity-change]');
      if (!button) return;
      const input = button.parentElement.querySelector('input[type="number"]');
      if (!input) return;
      const delta = Number(button.dataset.quantityChange);
      input.value = Math.max(Number(input.min || 1), Number(input.value || 1) + delta);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  initCustomSelects(scope = document) {
    scope.querySelectorAll('[data-custom-select]:not([data-custom-select-ready])').forEach((wrapper, index) => {
      const select = wrapper.querySelector('[data-custom-select-input]');
      if (!select) return;

      const listId = `CustomSelect-${Date.now()}-${index}`;
      const trigger = document.createElement('button');
      const menu = document.createElement('div');
      trigger.type = 'button';
      trigger.className = 'custom-select__trigger';
      trigger.setAttribute('aria-haspopup', 'listbox');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-controls', listId);
      menu.className = 'custom-select__menu';
      menu.id = listId;
      menu.setAttribute('role', 'listbox');
      menu.tabIndex = -1;

      Array.from(select.options).forEach((option, optionIndex) => {
        const item = document.createElement('button');
        item.type = 'button';
        item.className = 'custom-select__option';
        item.textContent = option.textContent;
        item.dataset.optionIndex = String(optionIndex);
        item.setAttribute('role', 'option');
        item.setAttribute('aria-selected', String(option.selected));
        item.disabled = option.disabled;
        menu.append(item);
      });

      const label = document.createElement('span');
      label.className = 'custom-select__value';
      const arrow = document.createElement('span');
      arrow.className = 'custom-select__arrow';
      arrow.setAttribute('aria-hidden', 'true');
      arrow.innerHTML = '<svg viewBox="0 0 16 16" fill="none"><path d="m4 6 4 4 4-4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';
      trigger.append(label, arrow);
      wrapper.append(trigger, menu);
      wrapper.classList.add('is-enhanced');
      wrapper.setAttribute('data-custom-select-ready', '');

      const sync = () => {
        label.textContent = select.selectedOptions[0]?.textContent || '';
        menu.querySelectorAll('[role="option"]').forEach((item, itemIndex) => {
          item.setAttribute('aria-selected', String(itemIndex === select.selectedIndex));
        });
      };
      const open = () => {
        this.closeCustomSelects();
        wrapper.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
      };
      const close = (returnFocus = false) => {
        wrapper.classList.remove('is-open');
        trigger.setAttribute('aria-expanded', 'false');
        if (returnFocus) trigger.focus();
      };
      const focusOption = (direction) => {
        const options = Array.from(menu.querySelectorAll('.custom-select__option:not(:disabled)'));
        if (!options.length) return;
        const activeIndex = options.indexOf(document.activeElement);
        let nextIndex = activeIndex;
        if (direction === 'first') nextIndex = 0;
        else if (direction === 'last') nextIndex = options.length - 1;
        else nextIndex = (activeIndex + direction + options.length) % options.length;
        options[nextIndex].focus();
      };

      trigger.addEventListener('click', () => {
        if (wrapper.classList.contains('is-open')) close();
        else open();
      });
      trigger.addEventListener('keydown', event => {
        if (!['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) return;
        event.preventDefault();
        if (!wrapper.classList.contains('is-open')) open();
        focusOption(event.key === 'ArrowUp' ? 'last' : 'first');
      });
      menu.addEventListener('click', event => {
        const item = event.target.closest('[data-option-index]');
        if (!item || item.disabled) return;
        select.selectedIndex = Number(item.dataset.optionIndex);
        sync();
        select.dispatchEvent(new Event('input', { bubbles: true }));
        select.dispatchEvent(new Event('change', { bubbles: true }));
        close(true);
      });
      menu.addEventListener('keydown', event => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          focusOption(event.key === 'ArrowDown' ? 1 : -1);
        } else if (event.key === 'Home' || event.key === 'End') {
          event.preventDefault();
          focusOption(event.key === 'Home' ? 'first' : 'last');
        } else if (event.key === 'Escape') {
          event.preventDefault();
          close(true);
        }
      });
      select.addEventListener('change', sync);
      sync();
    });
  }

  closeCustomSelects(returnFocus = false) {
    document.querySelectorAll('[data-custom-select].is-open').forEach(wrapper => {
      wrapper.classList.remove('is-open');
      const trigger = wrapper.querySelector('.custom-select__trigger');
      trigger?.setAttribute('aria-expanded', 'false');
      if (returnFocus) trigger?.focus();
    });
  }
}

document.addEventListener('DOMContentLoaded', () => new PaworaTheme());
